import dayjs from 'dayjs'
import { capitalize } from 'lodash'
import { makeObservable, observable, computed, action } from 'mobx'
import { DayjsFormats, downloadBlob, downloadFile, isLink } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { type IAvatarInfo, type IAvatarProps } from 'shared/ui'
import { Base } from 'models'
import {
  type automatedInfo,
  type IParamsCreateMessage,
  type IParamsUpdateMessage,
  type IResponseCallConferenceMessage,
  type IResponseIVRTransition,
  type IResponseMessage,
  type IResponseMessageFailedReason,
  type IResponseMessageIcon,
  type IResponseMessageSource,
  type IResponseMessageStatus,
  type IResponseRecord,
  type IResponseRecordTranscriptStatus,
} from 'entities/Message/api/types'
import {
  type IConversationsHistoryInfo,
  type IIVRTransition,
  type IMessageType,
} from 'entities/Message'
import { MessagesApi } from 'entities/Message/api/messages'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import { type IResponseUser } from 'entities/Users/api/types'
import { MessagePendingMention } from 'entities/Message/model/MessagePendingMention'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type Conversation } from 'entities/Conversation'
import { User } from 'entities/Users/model/User'
import { Inbox } from 'entities/Inbox/model/Inbox'

type IMessageProps = {
  message?: IResponseMessage
  payload?: {
    requestMessage: {
      body: string
      send_at?: string
      sent_at?: string
      media_url: RequestAttachment[]
      type?: IMessageType
      sending?: boolean
      created_at?: string
      undoPending?: boolean
      conversation_id?: number
    }
    client_id: string
  } | null
  conversationsHistoryInfo?: IConversationsHistoryInfo
}

export class Message extends Base {
  id: number
  conversation_id?: number
  client_id: number | string
  status?: IResponseMessageStatus = ''
  failed_reason?: IResponseMessageFailedReason = null
  received_at?: string
  sent_at?: string
  send_at?: string
  created_at: string
  sorted_date?: string
  body = ''
  user_id?: number
  contact_id: number
  sending = false
  undoPending = false
  is_voicemail?: boolean
  type?: IMessageType
  attachment_type?: string | null
  attachments_count?: number
  source: IResponseMessageSource = ''
  source_id: number | null = null
  broadcast_id?: number
  icon?: IResponseMessageIcon
  media: Attachment[]
  record: IResponseRecord | null = null
  callConference?: IResponseCallConferenceMessage
  has_tagging?: boolean
  stop_on_response?: boolean
  is_contact_timezone?: boolean
  is_sent_automatically?: boolean
  automatedInfo: automatedInfo | null
  is_hipaa = false
  pending_mentions_map: Map<number, MessagePendingMention> = new Map()
  conversations_history_info?: IConversationsHistoryInfo
  delivered_at = ''
  failed_at = ''
  ivr_transition?: IIVRTransition | null
  is_transferred?: boolean
  transfer_team?: {
    id: number
    name: string
  }

  constructor(
    { message, payload, conversationsHistoryInfo }: IMessageProps,
    private _entities: IEntitiesStore
  ) {
    super()

    if (message) {
      this.id = message.id
      this.client_id = message.client_id || message.id
      this.received_at = message.received_at
      this.sent_at = message.sent_at
      this.send_at = message.send_at
      this.created_at = message.created_at
      this.user_id = message.user_id
      this.contact_id = message.contact_id
      this.media =
        message.media?.map(
          (item) =>
            new Attachment({
              responseMedia: item,
              fromMediaUrl: true,
              isSave: !this.isOutbound,
            })
        ) || []
      this.body = this.initBody(message)
      this.status = this.initStatus(message)
      this.failed_reason = message.failed_reason
      this.type = message.type
      this.icon = message.icon
      this.record = message.record
      this.source = message.source
      this.source_id = message.source_id
      this.callConference = message.callConference
      this.is_voicemail = message.is_voicemail
      this.automatedInfo = message.automatedInfo
      this.is_hipaa = message.is_hipaa
      this.broadcast_id = message.broadcast_id
      this.has_tagging = message.has_tagging
      this.sorted_date = message.sorted_date || ''
      this.stop_on_response = message.stop_on_response
      this.is_contact_timezone = message.is_contact_timezone
      this.conversation_id = message.conversation_id
      this.is_sent_automatically = message.is_sent_automatically
      this.sending = false
      this.attachment_type = this.initAttachmentType(message)
      this.attachments_count = this.initAttachmentCount(message)
      this.pending_mentions_map = this.initPendingMentions(message.pending_mentions)
      this.delivered_at = message.delivered_at
      this.failed_at = message.failed_at
      this.ivr_transition = this.initIvrTransition(message.ivr_transition)
      this.is_transferred = message.is_transferred
      this.transfer_team = message.transfer_team
    } else {
      this.client_id = payload?.client_id || ''
      this.id = 0
      this.media =
        payload?.requestMessage.media_url.map(
          (item) =>
            new Attachment({
              requestAttachment: item,
            })
        ) || []
      this.body = payload?.requestMessage.body || ''
      this.send_at = payload?.requestMessage.send_at || ''
      this.contact_id = 0
      this.sending = payload?.requestMessage.sending ?? true
      this.undoPending = payload?.requestMessage.undoPending || false
      this.created_at = payload?.requestMessage.created_at || ''
      this.sent_at = payload?.requestMessage.sent_at || ''
      this.type = payload?.requestMessage.type
      this.conversation_id = payload?.requestMessage.conversation_id
      this.automatedInfo = null
    }

    if (conversationsHistoryInfo) {
      this.conversations_history_info = conversationsHistoryInfo
    }

    makeObservable(this, {
      sending: observable,
      body: observable,
      source: observable,
      status: observable,
      pending_mentions_map: observable,
      pending_mentions: computed,
      transcriptions: computed,
      isFailed: computed,
      isFailedTrial: computed,
      setTranscript: action.bound,
      record: observable,
    })
  }

  initIvrTransition = (data: IResponseIVRTransition | null): IIVRTransition | null => {
    if (!data) return null

    if (data.value === null || typeof data.value === 'string') {
      return {
        ...data,
        value: data.value,
      }
    }

    if (Array.isArray(data.value)) {
      return {
        ...data,
        value: data.value.map((item) => {
          return new User(item)
        }),
      }
    }

    if (typeof data === 'object' && 'type' in data.value) {
      return {
        ...data,
        value: new Inbox(data.value, this._entities),
      }
    }

    return null
  }

  initStatus = (message: IResponseMessage): IResponseMessageStatus => {
    if (message.source === 'salesmessage_chatbot') return 'delivered'

    return message.status
  }

  get pending_mentions() {
    return Array.from(this.pending_mentions_map.values())
  }

  initPendingMentions = (pending_mentions?: IResponseUser[]) => {
    if (!Array.isArray(pending_mentions)) return this.pending_mentions_map

    pending_mentions.forEach((item) => {
      this.pending_mentions_map.set(item.id, new MessagePendingMention(item, this._entities))
    })

    return this.pending_mentions_map
  }

  initAttachmentType = (message: IResponseMessage) => {
    if (message.attachment_type) return message.attachment_type

    return this.media.length ? this.media[0].content_type : null
  }

  initAttachmentCount = (message: IResponseMessage) => {
    if (message.attachments_count) return message.attachments_count

    return this.media.length
  }

  setTranscript = (message: IResponseMessage) => {
    this.body = message.body || ''
    this.status = message.status

    if (this.record) {
      this.record = {
        ...this.record,
        ...message.record,
      }
    }
  }

  setTranscriptStatus = (status: string) => {
    this.status = status
  }

  setRecordTranscriptStatus = (status: IResponseRecordTranscriptStatus) => {
    if (!this.record) return
    this.record.transcript_status = status
  }

  get recordTranscriptStatus() {
    return this.record?.transcript_status
  }

  get contact_tz() {
    return this._entities.contactsStore.getItem(this.contact_id)?.timezone
  }

  get first_name() {
    if (this.isOutbound) {
      return this._entities.usersStore.getItem(this.user_id)?.first_name
    }
    const first_name = this._entities.contactsStore.getItem(this.contact_id)?.first_name
    return first_name?.startsWith('+') ? undefined : first_name
  }

  get phone_number() {
    if (this.isOutbound) {
      return this._entities.usersStore.getItem(this.user_id)?.number
    }

    return this._entities.contactsStore.getItem(this.contact_id)?.number
  }

  get isCallConference() {
    return Boolean(this.callConference)
  }

  get isAirCall() {
    return this.body.startsWith('<a href') && this.body.includes('aircall.io/calls')
  }

  get airCallLink() {
    if (this.isAirCall) {
      const match = this.body.match(/href=".*?"/)
      if (match?.length) {
        return match[0].replace('href=', '').replace(/"/g, '')
      }
    }
  }

  get statusDisplayName() {
    if (this.status === 'faked') return ''
    if (this.status === 'failed_trial') return 'Failed'
    if (this.status === 'queued') return 'Queued on carrier side'
    if (this.isQuietHours) return 'Quiet hours'

    return capitalize(this.status)
  }

  get avatarProps(): Omit<IAvatarProps, 'info'> | undefined {
    if (this.source === 'salesmessage_chatbot') {
      return {
        is_full_width: false,
        size: 24,
        iconProps: {
          fontSize: 24,
        },
      }
    }

    return undefined
  }

  get conversation(): Conversation | undefined {
    return this._entities.conversationStore.getItem(this.conversation_id)
  }

  get contact() {
    return this._entities.contactsStore.getItem(this.contact_id)
  }

  get avatarInfo(): IAvatarInfo | null {
    if (this.source === 'salesmessage_chatbot') {
      return {
        icon: 'calendlyMeeting',
        color: 'transparent',
      }
    }

    if (this.isOutbound) {
      if (this.user_id) {
        return this._entities.usersStore.getItem(this.user_id)?.avatarInfo || null
      }

      return this._entities.usersStore.user?.avatarInfo || null
    }

    return this._entities.contactsStore.getItem(this.contact_id)?.avatarInfo || null
  }

  get isOutbound() {
    if (this.source === 'salesmessage_chatbot') return true

    return !this.received_at
  }

  get sortDateTime() {
    return dayjs(this.sorted_date || this.date).valueOf()
  }

  get date() {
    if (this.failed_at) return this.failed_at
    if (this.delivered_at) return this.delivered_at
    if (this.sorted_date) return this.sorted_date
    if (this.isOutbound) return this.sent_at || this.send_at || this.created_at

    return this.received_at
  }

  get day() {
    return uiStore.dayjs(this.date).format(DayjsFormats.dateSort)
  }

  get images() {
    return this.media.filter(
      (item) => item.type === 'image' || (item.type === 'gif' && !item.isPreviewGif)
    )
  }

  async downloadMedia(...types: Attachment['type'][]) {
    const mediaAttachments = this.media.filter((attachment) => types.includes(attachment.type))

    if (mediaAttachments.length > 1) {
      const { data } = await MessagesApi.getMessageMediaArchive(this.id)
      downloadBlob(data, `Salesmsg_${dayjs().format('YYYY-MM-DD')}.zip`)
      const ids = mediaAttachments.map((item) => Number(item.id))
      this.onTrackDownloadMedia(ids)
    } else if (mediaAttachments[0].source) {
      downloadFile(mediaAttachments[0].source)
      this.onTrackDownloadMedia([Number(mediaAttachments[0].id)])
    }
  }

  onTrackDownloadMedia = (ids: number[]) => {
    if (this._entities.organizationStore.isHipaaCompliant) {
      this._entities.organizationStore.handleTrackOrganizationActivity({
        event: 'phi.file.downloaded',
        data: {
          file_ids: ids,
        },
      })
    }
  }

  get previewGifs() {
    return this.media.filter((item) => item.type === 'previewGif')
  }

  get videos() {
    return this.media.filter((item) => item.type === 'video')
  }

  get otherAttachments() {
    return this.media.filter(
      (item) =>
        item.type !== 'image' &&
        item.type !== 'gif' &&
        item.type !== 'previewGif' &&
        item.type !== 'video'
    )
  }

  get transcriptStatus() {
    if (!this.record) return null

    return this.record.transcript_status || null
  }

  get transcriptions() {
    if (this.status === 'transcript') {
      if (this.record?.diarization?.length) {
        return this.record?.diarization
      }

      if (this.body) {
        return [
          {
            id: 0,
            speaker_label: '',
            speaker_number: 0,
            text: this.body,
          },
        ]
      }

      return []
    }

    return []
  }

  get summary() {
    if (!this.record?.ai_summary?.length) return []

    return this.record.ai_summary
  }

  get forwardInfo() {
    if (this.type === 'call' && this.body.includes('Call forwarded to')) {
      // "Call forwarded to (844) 571-0440 - 1 minute"
      const rightPart = this.body.split('Call forwarded to ')[1]
      const info = rightPart?.split(' - ')
      if (info) {
        return {
          phoneNumber: info[0],
          duration: info[1]?.replace('minute', 'min'),
        }
      }
    }

    return null
  }

  get isOnlyRecord() {
    if (this.status === 'record' || this.status === 'transcript') {
      return !!this.record
    }
    return false
  }

  get isHideAvatar() {
    if (this.isAirCall) {
      return this.icon === 'icon_voicemail'
    }

    return this.isOnlyRecord
  }

  get copyText() {
    if (this.type === 'sms' || this.type === 'mms') {
      return this.body || ''
    }
    return ''
  }

  get fromBroadcast() {
    return this.isOutbound && (this.source === 'broadcast' || this.broadcast_id)
  }

  get isAutomated() {
    return this.isOutbound && (this.source === 'opt_in_confirmation' || this.is_sent_automatically)
  }

  get isOnlyVCard() {
    return this.media.length === 1 && this.media[0].type === 'vcard'
  }

  get links() {
    const links: string[] = []
    this.body.split(/\n/).forEach((text) =>
      text.split(' ').forEach((word) => {
        if (isLink(word)) {
          links.push(word)
        }
      })
    )
    return links
  }

  get bodyParse() {
    // Added for Conference call timeline
    if (this.body.includes('Conference call ended')) {
      return 'Conference call ended'
    }

    return this.body
  }

  get is_schedule() {
    return (
      this.send_at &&
      !this.received_at &&
      ((!this.sent_at && !this.isStatusRetry) || this.isQuietHours)
    )
  }

  get isStatusRetry() {
    return this.status === 'retry' || this.status === 'retry_manual'
  }

  get isFailed() {
    return (
      this.status === 'failed' ||
      this.status === 'undelivered' ||
      this.status === 'billing_declined' ||
      this.isFailedTrial
    )
  }

  get isFailedTrial() {
    return this.status === 'failed_trial'
  }

  get isDelivered() {
    return this.status === 'delivered'
  }
  get isQueued() {
    return this.status === 'queued'
  }
  get isCreated() {
    return this.status === 'created'
  }

  get isSent() {
    return this.status === 'sent'
  }

  get isQuietHours() {
    if (this._entities.featureFlagsStore.quiet_hours) {
      return this.status === 'quiet_hours'
    }

    return false
  }

  get isRetryError() {
    if (this.failed_reason === '30006') return false

    return !this.isFailedTrial
  }

  get isNote() {
    return this.type === 'note'
  }

  get isMms() {
    return this.type === 'mms'
  }

  get messageRequestData(): IParamsCreateMessage {
    return {
      media_url: this.media.map((attachment) => new RequestAttachment(attachment)),
      message: this.body,
      send_at: this.send_at,
      stop_on_response: this.stop_on_response || false,
      is_contact_timezone: this.is_contact_timezone || false,
      type: null,
    }
  }

  get paramsUpdateMessage(): IParamsUpdateMessage {
    return {
      media_url: this.media.map((attachment) => new RequestAttachment(attachment)),
      body: this.body,
      send_at: this.send_at,
      stop_on_response: this.stop_on_response || false,
      is_contact_timezone: this.is_contact_timezone || false,
      manual_created: true,
    }
  }

  get isTransferred() {
    return this.is_transferred
  }

  get transferTeamName() {
    return this.transfer_team?.name
  }

  get isServerCommand() {
    // List of server commands
    // ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'TESTQUIT'];
    // ['START', 'YES', 'UNSTOP', 'Y'];
    // ['HELP', 'INFO'];

    const status = this.body.toLowerCase()

    return Boolean(
      status === 'start' ||
        status === 'stop' ||
        status === 'stopall' ||
        status === 'unsubscribe' ||
        status === 'cancel' ||
        status === 'end' ||
        status === 'quit' ||
        status === 'testquit' ||
        status === 'yes' ||
        status === 'unstop' ||
        status === 'y' ||
        status === 'help' ||
        status === 'info'
    )
  }

  get isConversationsHistory() {
    return Boolean(this.conversations_history_info)
  }
  get isExternalConversation() {
    return Boolean(this.conversations_history_info?.is_external)
  }
  get teamName() {
    return this.conversations_history_info?.teamName || this.transferTeamName || ''
  }
  get teamId() {
    return this.conversations_history_info?.teamId
  }

  get isMeet() {
    return this.source === 'salesmessage_chatbot'
  }

  initBody = (message: IResponseMessage) => {
    if (message.source === 'salesmessage_chatbot') {
      return message.body_raw || ''
    }

    if (message.type === 'note') return message.body_template || message.body || ''

    return message.body_raw || message.body || ''
  }

  get ivrTransition() {
    return this.ivr_transition
  }
}
